import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardCheck,
  Building2,
  AlertCircle,
  ArrowUpRight,
  Activity,
  CalendarDays,
  Plus,
  Check,
  Inbox,
  BriefcaseBusiness,
  CreditCard,
  Video,
  History,
} from 'lucide-react';
import {
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../services/api.jsx';
import {
  Barres, Jauge, Tendance, Anneau, Radar, Entonnoir, Calendrier,
} from '../components/graphiques';
import Miniligne from '../components/Miniligne';
import {
  PROFESSIONALISM_LEVELS,
  resolveProfessionalismInfo,
} from '../utils/professionalism';
import { useDroits } from '../context/Droits';
import {
  annoncerSucces, AvisDonneesPartielles, EtatErreur, Squelette, SqueletteGraphique, SqueletteListe, SqueletteTexte,
} from '../etats';

/* Les teintes d'avatar du fil d'activité, une par état. La couleur n'est
   jamais seule : l'étiquette à droite dit la même chose en toutes
   lettres. */
const TONS_ACTIVITE = {
  ok:      'bg-[var(--vert-pale)] text-[var(--vert-fonce)]',
  attente: 'bg-[var(--attention-fond)] text-[var(--attention)]',
  refus:   'bg-[var(--alerte-fond)] text-[var(--alerte)]',
  cours:   'bg-[var(--nuit)] text-[var(--accent)]',
};

/* « 2 h », « 5 j » : l'âge se lit d'un coup d'œil, une date complète
   demande un calcul mental à chaque ligne. */
function depuis(iso) {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `${heures} h`;
  const jours = Math.round(heures / 24);
  return jours < 31 ? `${jours} j` : `${Math.round(jours / 30)} mois`;
}

/* « aujourd'hui, 14:00 – 15:30 » plutot qu'une date ISO : la date
   complete n'apporte rien quand la seance est dans l'heure, et on la
   donne quand elle est plus lointaine. */
function quandSeance(debut, dureeMinutes) {
  const d = new Date(debut);
  const fin = new Date(d.getTime() + (dureeMinutes || 60) * 60000);
  const heure = (x) => x.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const jour = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const zero = new Date();
  const ecart = Math.round((jour - new Date(zero.getFullYear(), zero.getMonth(), zero.getDate())) / 86400000);
  const quand = ecart === 0 ? "aujourd'hui"
    : ecart === 1 ? 'demain'
    : d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return `${quand}, ${heure(d)} – ${heure(fin)}`;
}

/* Le serveur nomme l'icone de chaque ligne « A traiter ». On ne lui
   laisse pas choisir librement : seules ces quatre-la existent, et un
   nom inconnu retombe sur la bannette plutot que de casser le rendu. */
const ICONES_A_TRAITER = {
  Inbox,
  BriefcaseBusiness,
  ClipboardCheck,
  CreditCard,
};

/* Les couleurs de l'anneau, dans l'ordre du modele. Au-dela de cinq
   parts les angles deviennent indiscernables : le reste est regroupe. */
const TEINTES_ANNEAU = ['#5EBD4F', '#102538', '#A7DA9C', '#22405C', '#EAF6E7'];


/* ─────────────────────────────────────────────────────
   Hook count-up
───────────────────────────────────────────────────── */
function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined) return;
    const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ''));
    if (Number.isNaN(numeric)) { setValue(target); return; }

    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * numeric);
      setValue(current);
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
}

/* ─────────────────────────────────────────────────────
   Skeleton loader
───────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="animate-pulse bg-[var(--bg-card)] shadow-carte rounded-cadre p-6 flex flex-col gap-3 h-[148px]">
      <div className="w-10 h-10 rounded-pastille bg-[var(--bg-main)]" />
      <div className="h-8 w-24 rounded-pastille bg-[var(--bg-main)] mt-auto" />
      <div className="h-3 w-16 rounded-controle bg-[var(--bg-card)]" />
    </div>
  );
}

function SkeletonBlock({ className = '' }) {
  return (
    <div className={`animate-pulse bg-[var(--bg-card)] shadow-carte rounded-cadre ${className}`} />
  );
}

/* ─────────────────────────────────────────────────────
   KPI Card
───────────────────────────────────────────────────── */
function KpiCard({ icon: Icon, label, value, sub, trend, index,
                   variante = 'clair', courbe, lien, ton, unite, brut, enChargement = false }) {
  const numericTarget = useMemo(() => {
    if (value === null || value === undefined) return 0;
    const n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isNaN(n) ? 0 : n;
  }, [value]);

  const animated = useCountUp(numericTarget);

  const displayValue = useMemo(() => {
    if (value === null || value === undefined) return '—';
    // `brut` : la valeur est déjà mise en forme (séparateurs de milliers,
    // virgule décimale). L'animer la reconstruirait sans sa mise en forme,
    // et « 1,24 » finirait en « 1 ».
    if (brut) return value;
    const str = String(value);
    if (str.includes('%')) return `${animated}%`;
    return animated;
  }, [animated, value, brut]);

  const sombre = variante === 'sombre';

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.07, ease: [0.22, 0.61, 0.36, 1] }}
      /* La carte chiffrée du modèle : l'intitulé en 15 px, le chiffre en
         46, l'écart et la mini-courbe collés en bas. Ni icône colorée ni
         halo — le modèle s'appuie sur le blanc, l'ombre et la taille.

         Une seule carte par écran passe en sombre : c'est elle qui donne
         le point d'entrée du regard. Deux se neutraliseraient. */
      className={`relative overflow-hidden rounded-carte p-5 pb-4 flex flex-col
                  min-h-[176px] flex-[1_1_230px]
                  ${sombre ? 'sur-sombre text-white' : 'bg-[var(--bg-card)] shadow-carte'}`}
      style={sombre ? {
        background: 'radial-gradient(120% 130% at 100% 100%, #1E5B3B 0%, #12304A 48%, #0B1620 100%)',
      } : undefined}
    >
      {/* Les cercles concentriques du modèle, hors du flux et sans
          interception du pointeur. */}
      {sombre && (
        <svg width="260" height="260" viewBox="0 0 260 260" aria-hidden="true"
             className="absolute -right-[110px] -bottom-[120px] pointer-events-none">
          {[36, 56, 76, 96, 116].map((r, i) => (
            <circle key={r} cx="130" cy="130" r={r} fill="none"
                    stroke={`rgba(94,189,79,${[0.5, 0.36, 0.25, 0.16, 0.09][i]})`} />
          ))}
        </svg>
      )}

      <div className="relative flex items-center gap-2.5">
        <p className={`m-0 flex-1 text-[15px] font-medium leading-snug
                       ${sombre ? '' : 'text-[var(--text-bold)]'}`}>
          {label}
        </p>
        {/* Le modèle met ici un bouton, pas une vignette décorative : la
            carte est un raccourci vers sa liste. */}
        {lien ? (
          <button type="button" onClick={lien.aller} aria-label={lien.libelle}
                  className="w-9 h-9 shrink-0 rounded-controle grid place-items-center
                             bg-[var(--bg-card)] text-[var(--text-bold)]
                             hover:bg-[var(--accent)] transition-colors">
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </button>
        ) : (
          <span aria-hidden="true"
                className={`shrink-0 w-9 h-9 grid place-items-center rounded-controle
                            ${sombre ? 'bg-[var(--bg-main)] text-[var(--accent)]'
                                     : 'bg-[var(--bg-main)] text-[var(--text-muted)]'}`}>
            <Icon className="w-[17px] h-[17px]" strokeWidth={1.5} />
          </span>
        )}
      </div>

      {/* 46 px, 600, interlettrage −4 %, chiffres tabulaires : sans eux, la
          valeur tressaute à chaque incrément de l'animation. */}
      {/* En attendant le chiffre, sa forme : un « 0 » serait lu comme un résultat. */}
      {enChargement ? (
        <Squelette className="relative mt-3.5 h-[clamp(2rem,5vw,2.875rem)] w-28" />
      ) : (
      <p className={`relative m-0 mt-3.5 text-[clamp(2rem,5vw,2.875rem)] font-semibold
                     leading-none tracking-[-.04em] tabular-nums
                     ${sombre ? '' : 'text-[var(--text-bold)]'}`}>
        {displayValue}
        {unite && (
          <span className="ml-1.5 text-[16px] font-medium tracking-normal
                           text-[var(--text-muted)]">
            {unite}
          </span>
        )}
      </p>
      )}

      <div className="relative flex items-end justify-between gap-2.5 mt-auto pt-3.5">
        <span className={`doux inline-flex items-center gap-1.5 text-[12.5px] whitespace-nowrap
                          ${sombre ? '' : 'text-[var(--text-muted)]'}`}>
          {/* `null` veut dire « non mesurable » : le serveur le renvoie
              quand la période précédente est vide. On n'affiche alors
              aucune pastille, plutôt qu'un « 0 % » qui affirmerait une
              stabilité que personne n'a constatée. */}
          {trend !== undefined && trend !== null && (
            <span className={`inline-flex items-center gap-[3px] h-[22px] px-[7px] rounded-[6px]
                              font-semibold
              ${sombre
                ? 'bg-[rgba(94,189,79,.22)] text-[#9ED591]'
                : trend >= 0
                  ? 'bg-[var(--vert-pale)] text-[var(--vert-fonce)]'
                  : 'bg-[var(--alerte-fond)] text-[var(--alerte)]'}`}>
              <ArrowUpRight className={`w-3.5 h-3.5 ${trend < 0 ? 'rotate-180' : ''}`} strokeWidth={2} />
              {Math.abs(trend)} %
            </span>
          )}
          {/* Le ton d'alerte du modèle : la carte qui réclame une action
              le dit par une pastille ambre, pas par une couleur de fond. */}
          {ton === 'attention' && (
            <span className="inline-flex items-center gap-[5px] h-[22px] px-2 rounded-[6px]
                             bg-[var(--attention-fond)] text-[var(--attention)] font-semibold">
              <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} />
              à traiter
            </span>
          )}
          {!enChargement && sub}
        </span>
        {courbe && (
          <Miniligne donnees={courbe} sombre={sombre}
                     couleur={sombre ? '#5EBD4F' : 'var(--accent)'} />
        )}
      </div>
    </motion.article>
  );
}






/* ─────────────────────────────────────────────────────
   Dashboard principal
───────────────────────────────────────────────────── */
export default function Dashboard() {
  // Les boutons d'export ne s'affichent que si le droit est ouvert.
  const { peut } = useDroits();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [essai, setEssai] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminApi
      .getGlobalStats()
      .then(setStats)
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [essai]);

  /* Données dérivées */
  const levelData = useMemo(() => {
    const source = Array.isArray(stats?.chartData)
      ? stats.chartData
      : (Array.isArray(stats?.barChartData) ? stats.barChartData : []);

    if (!source.length) return [];

    const buckets = PROFESSIONALISM_LEVELS.map((level) => ({
      ...level,
      category: level.category,
      label: level.label,
      levelLabel: level.level,
      audits: 0,
      percentage: 0,
      fill: level.hex,
    }));

    source.forEach((entry, index) => {
      const audits = Number(entry?.audits ?? entry?.value ?? entry?.count ?? 0);
      const info = resolveProfessionalismInfo({ rawLevel: entry?.name, score: entry?.score, index });
      const bucket = buckets.find((item) => item.category === info.category);
      if (!bucket) return;
      bucket.audits += Number.isFinite(audits) ? Math.max(0, audits) : 0;
    });

    const total = buckets.reduce((sum, item) => sum + item.audits, 0);
    return buckets.map((item) => ({
      ...item,
      percentage: total > 0 ? (item.audits / total) * 100 : 0,
    }));
  }, [stats]);

  /* La moyenne de la répartition, arrondie. Elle sert de repère tracé en
     travers du graphique : le catalogue du modèle le demande pour les
     barres, et sans elle une hauteur ne se compare à rien. */
  /* ── Ce que portent les cartes chiffrées ──────────────────────────
     Les mini-courbes reprennent les séries DÉJÀ chargées pour le
     graphique temporel : on ne demande rien de plus au serveur pour
     dessiner 108 px de courbe. Sans série, pas de courbe — la carte
     reste juste, elle ne montre pas une ligne inventée. */
  /* Les écarts sont calculés par le serveur, sur les mêmes séries : les
     recalculer ici donnerait deux vérités pour un même chiffre. `null`
     signifie « pas mesurable », et la pastille ne s'affiche pas. */
  /* La session admin n'est pas passée par l'Outlet : elle vit dans le
     stockage local, posée par App.jsx à la connexion. On la lit une fois,
     en tolérant un contenu illisible plutôt qu'en cassant la page. */
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('asso_admin_user') || 'null'); }
    catch { return null; }
  }, []);

  /* ── Le tableau de bord du modèle ─────────────────────────────────
     Un seul appel pour les huit blocs : les chiffres d'une même page
     viennent ainsi tous du même instant. La profondeur choisie en haut
     le relance — c'est le serveur qui découpe le temps, pas le
     navigateur, car lui seul sait ce qu'il y a en base. */
  const [periode, setPeriode] = useState('12mois');
  const [tdb, setTdb] = useState(null);
  const [tdbEnCours, setTdbEnCours] = useState(true);
  const [tdbErreur, setTdbErreur] = useState(null);
  const [tdbEssai, setTdbEssai] = useState(0);

  /* Ce second appel échouait en silence : les widgets affichaient alors des
     zéros plausibles. L'échec est désormais dit (données partielles), avec
     de quoi réessayer ; changer de période garde l'écran en place. */
  useEffect(() => {
    let vivant = true;
    setTdbEnCours(true);
    adminApi.getTableauDeBord(periode)
      .then((d) => { if (vivant) { setTdb(d); setTdbErreur(null); } })
      .catch((e) => { if (vivant) setTdbErreur(e); })
      .finally(() => { if (vivant) setTdbEnCours(false); });
    return () => { vivant = false; };
  }, [periode, tdbEssai]);

  /* Le premier chargement de ce second appel : les blocs qui en dépendent
     montrent leur forme, pas un « rien » qu'ils n'ont pas encore constaté.
     Un changement de période garde, lui, l'écran en place. */
  const tdbAttendu = !tdb && tdbEnCours;

  const issue = tdb?.issue_demandes || { acceptees: 0, rejetees: 0, en_attente: 0, total: 0 };
  const aTraiter = tdb?.a_traiter || [];
  const radar = tdb?.radar || { axes: [], seuil: 65 };
  const parcours = tdb?.parcours || [];
  const seances = tdb?.seances || { semaines: [], total: 0 };
  const prochaine = tdb?.prochaine_seance || null;
  const activite = tdb?.activite || [];

  /* Le compte à rebours de la prochaine séance. Il se recalcule chaque
     minute et non chaque seconde : à cette échelle la seconde n'apporte
     rien, et un rendu par seconde réanime toute la page pour rien. */
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    if (!prochaine) return undefined;
    const minuterie = setInterval(() => setMaintenant(Date.now()), 60000);
    return () => clearInterval(minuterie);
  }, [prochaine]);

  const compteARebours = useMemo(() => {
    if (!prochaine) return '';
    const reste = new Date(prochaine.debut).getTime() - maintenant;
    if (reste <= 0) return 'maintenant';
    const minutes = Math.floor(reste / 60000);
    const jours = Math.floor(minutes / 1440);
    if (jours >= 1) return `dans ${jours} j`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h ? `dans ${h} h ${String(m).padStart(2, '0')}` : `dans ${m} min`;
  }, [prochaine, maintenant]);

  /* Au plus cinq parts dans l'anneau : au-delà, les angles deviennent
     indiscernables et la forme ment sur les proportions. Le reste est
     regroupé sous « Autres formules » — un total juste vaut mieux
     qu'une part illisible. */
  const parFormule = useMemo(() => {
    const brut = tdb?.par_formule || [];
    const tete = brut.slice(0, 4).map((f, i) => ({
      code: f.code, nom: f.nom, valeur: f.nombre, couleur: TEINTES_ANNEAU[i],
    }));
    const reste = brut.slice(4);
    if (reste.length) {
      tete.push({
        code: '_autres',
        nom: `Autres formules (${reste.length})`,
        valeur: reste.reduce((s, f) => s + f.nombre, 0),
        hachure: true,
      });
    }
    return tete;
  }, [tdb]);

  const cartes = tdb?.cartes || {};

  /* Les montants en FCFA se lisent mal au-delà du million : le modèle
     bascule alors sur « 1,24 M FCFA ». L'unité part dans un exposant
     plus petit, pour que le chiffre garde ses 46 px. */
  const encaisse = useMemo(() => {
    const v = Number(cartes.encaissements?.valeur) || 0;
    return v >= 1e6
      ? { chiffre: (v / 1e6).toFixed(2).replace('.', ','), unite: 'M FCFA' }
      : { chiffre: Math.round(v).toLocaleString('fr-FR'), unite: 'FCFA' };
  }, [cartes.encaissements]);

  const aujourdhui = useMemo(() => new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  }), []);

  /* L'export reprend ce qui est à l'écran, au format que tout tableur
     ouvre. Pas d'appel serveur : les chiffres sont déjà là. */
  const exporter = () => {
    const lignes = [['Niveau', 'Audits'], ...levelData.map((n) => [n.name, n.audits])];
    const csv = lignes.map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `assopro-tableau-de-bord-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    annoncerSucces('Tableau de bord exporté (CSV).');
  };

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <div className="h-10 w-64 rounded-pastille animate-pulse bg-[var(--bg-card)] mb-2" />
          <div className="h-4 w-48 rounded-pastille animate-pulse bg-[var(--bg-card)]" />
        </div>
        {/* Deux par ligne des le telephone : une seule colonne donnait des
          cartes enormes et un chiffre perdu au milieu du vide. */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <SkeletonBlock className="xl:col-span-2 h-[440px]" />
          <SkeletonBlock className="h-[440px]" />
        </div>
        <SkeletonBlock className="h-[460px]" />
        <SkeletonBlock className="h-[340px]" />
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <EtatErreur erreur={error} onReessayer={() => setEssai((n) => n + 1)} />
      </div>
    );
  }

  /* ── Render principal ── */
  return (
    <div className="space-y-8 animate-in" aria-busy={tdbEnCours || undefined}>
      {tdbErreur && (
        <AvisDonneesPartielles manquantes={['indicateurs de la période', 'graphiques', 'éléments à traiter']}
                               onReessayer={() => setTdbEssai((n) => n + 1)} />
      )}

      {/* ── En-tête ──────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
        className="flex flex-wrap items-end gap-4 mb-2.5"
      >
        {/* Le modèle n'écrit pas le titre en deux couleurs : un seul ton
            d'encre, et c'est la taille qui porte la hiérarchie. */}
        <div className="flex-[1_1_320px] min-w-0">
          <h1 className="titre-page">Tableau de bord</h1>
          <p className="mt-2 text-[14.5px] text-[var(--text-muted)] text-pretty">
            Bonjour {user?.username || 'à vous'} — activité de la plateforme au {aujourdhui}.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* La profondeur d'observation. Elle pilote les quatre
              graphiques ET les courbes des cartes : un seul réglage pour
              toute la page, sinon deux blocs côte à côte parleraient de
              deux périodes différentes sans le dire. */}
          <div className="segmente" role="group" aria-label="Période d'observation">
            {[['mois', 'Mois'], ['trim', 'Trimestre'], ['12mois', '12 mois']].map(([cle, libelle]) => (
              <button key={cle} type="button" onClick={() => setPeriode(cle)}
                      aria-pressed={periode === cle} className="whitespace-nowrap">
                {libelle}
              </button>
            ))}
          </div>

          <button type="button" onClick={() => navigate('/projets?nouveau=1')} className="act">
            <Plus className="w-[17px] h-[17px]" strokeWidth={2} />
            Nouveau projet
          </button>
          {peut('dashboard', 'exporter') && (
            <button type="button" onClick={exporter} className="act act-contour">
              Exporter
            </button>
          )}
        </div>
      </motion.div>

      {/* ── Les indicateurs ──────────────────────────────────────────
          Une rangée souple plutôt qu'une grille : le modèle donne à
          chaque carte une base de 230 px et les laisse se répartir. Une
          grille à quatre colonnes fixes écrasait les cartes entre 1024 et
          1280 px, où quatre chiffres de 46 px ne tiennent pas. */}
      <div className="flex flex-wrap gap-3.5">
        <KpiCard
          index={0}
          variante="sombre"
          icon={Building2}
          label="Organisations accompagnées"
          value={cartes.organisations?.valeur ?? stats?.activeOrgs ?? 0}
          sub="structures suivies"
          trend={cartes.organisations?.ecart}
          courbe={cartes.organisations?.serie}
          lien={{ libelle: 'Voir les organisations', aller: () => navigate('/users') }}
        />
        <KpiCard
          index={1}
          icon={ClipboardCheck}
          label="Diagnostics réalisés"
          value={cartes.diagnostics?.valeur ?? stats?.totalAudits ?? 0}
          sub="depuis l'ouverture"
          trend={cartes.diagnostics?.ecart}
          courbe={cartes.diagnostics?.serie}
          lien={{ libelle: 'Voir les diagnostics', aller: () => navigate('/diagnostics') }}
        />
        {/* Les demandes en attente : le seul des quatre chiffres sur
            lequel on AGIT, d'où la pastille d'alerte quand il monte. */}
        <KpiCard
          index={2}
          icon={Inbox}
          label="Demandes en attente"
          value={cartes.demandes?.valeur ?? 0}
          ton={(cartes.demandes?.valeur ?? 0) > 0 ? 'attention' : undefined}
          sub={(cartes.demandes?.valeur ?? 0) > 0 ? '' : 'rien en attente'}
          trend={cartes.demandes?.ecart}
          courbe={cartes.demandes?.serie}
          lien={{ libelle: 'Voir les demandes', aller: () => navigate('/demandes') }}
          enChargement={tdbAttendu}
        />
        <KpiCard
          index={3}
          icon={CreditCard}
          label="Encaissements"
          value={encaisse.chiffre}
          unite={encaisse.unite}
          brut
          sub="paiements confirmés"
          trend={cartes.encaissements?.ecart}
          courbe={cartes.encaissements?.serie}
          lien={{ libelle: 'Voir les paiements', aller: () => navigate('/payments') }}
          enChargement={tdbAttendu}
        />
      </div>

      {/* ── Le bloc central du modèle ────────────────────────────────
          Deux colonnes : à gauche les quatre graphiques par paires, à
          droite ce qui attend une décision. Le rapport 3/1 est celui du
          modèle ; en dessous de 900 px les colonnes se replient l'une
          sous l'autre sans règle supplémentaire, `flex-wrap` suffit. */}
      <div className="flex flex-wrap gap-3.5 items-stretch">
        <div className="flex-[3_1_620px] min-w-0 flex flex-col gap-3.5">

          <div className="flex flex-wrap gap-3.5">
            {/* Encaissements */}
            <article className="flex-[2_1_380px] min-w-0 rounded-carte bg-[var(--bg-card)]
                                shadow-carte px-[22px] pt-5 pb-4">
              <div className="flex flex-wrap items-start gap-3 mb-1.5">
                <div className="flex-[1_1_180px] min-w-0">
                  <h2 className="titre-carte">Encaissements</h2>
                  <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                    En FCFA, par {tdb?.unite || 'mois'}
                  </p>
                </div>
                {/* La légende énonce la règle de lecture des barres. Sans
                    elle, le hachuré ressemble à un défaut d'affichage. */}
                <div className="flex flex-wrap gap-3 text-[12px] text-[var(--text-muted)]">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-controle bg-[#102538]" />Record
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-controle bg-[#5EBD4F]" />Au-dessus de la moyenne
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-controle shadow-[inset_0_0_0_1px_#D5DFD9]"
                          style={{ background: 'repeating-linear-gradient(45deg,#B9C7BF 0 2px,#FFFFFF 2px 4px)' }} />
                    En dessous
                  </span>
                </div>
              </div>
              {tdbAttendu
                ? <SqueletteGraphique className="h-[236px] mt-3" />
                : <Barres donnees={tdb?.barres || []} hauteur={236} suffixe="FCFA" />}
            </article>

            {/* Issue des demandes */}
            <article className="flex-[1_1_250px] min-w-0 rounded-carte bg-[var(--bg-card)]
                                shadow-carte px-[22px] pt-5 pb-[18px] flex flex-col">
              <h2 className="titre-carte">Issue des demandes</h2>
              {tdbAttendu ? <Squelette className="h-3 w-40 mt-1.5" /> : (
              <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                {issue.total} demande{issue.total > 1 ? 's' : ''} reçue{issue.total > 1 ? 's' : ''} sur la période
              </p>
              )}
              <div className="mt-auto pt-3.5">
                {tdbAttendu ? (
                  <Squelette className="h-24 w-full !rounded-pastille" />
                ) : issue.total > 0 ? (
                  <Jauge segments={[
                    { nom: 'Acceptées', valeur: issue.acceptees, couleur: '#5EBD4F' },
                    { nom: 'En attente', valeur: issue.en_attente, couleur: '#102538' },
                    { nom: 'Rejetées', valeur: issue.rejetees, hachure: true },
                  ]} />
                ) : (
                  <p className="text-[13px] text-[var(--text-muted)] text-center py-8">
                    Aucune demande sur la période.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-y-2 gap-x-3.5 mt-3.5
                              text-[12px] text-[var(--text-main)]">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-controle bg-[#5EBD4F]" />Acceptées
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-controle bg-[#102538]" />En attente
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-controle shadow-[inset_0_0_0_1px_#D5DFD9]"
                        style={{ background: 'repeating-linear-gradient(45deg,#B9C7BF 0 2px,#FFFFFF 2px 4px)' }} />
                  Rejetées
                </span>
              </div>
            </article>
          </div>

          <div className="flex flex-wrap gap-3.5">
            {/* Diagnostics réalisés */}
            <article className="flex-[2_1_380px] min-w-0 rounded-carte bg-[var(--bg-card)]
                                shadow-carte px-[22px] pt-5 pb-4">
              <h2 className="titre-carte">Diagnostics réalisés</h2>
              <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                Par {tdb?.unite || 'mois'}, sur la période retenue
              </p>
              <div className="mt-2">
                {tdbAttendu
                  ? <SqueletteGraphique className="h-[240px]" />
                  : <Tendance donnees={tdb?.tendance || []} hauteur={240} />}
              </div>
            </article>

            {/* Accompagnements par formule */}
            <article className="flex-[1_1_250px] min-w-0 rounded-carte bg-[var(--bg-card)]
                                shadow-carte px-[22px] pt-5 pb-[18px] flex flex-col">
              <h2 className="titre-carte">Accompagnements par formule</h2>
              <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                Projets ouverts, toutes périodes
              </p>
              {tdbAttendu ? (
                <div className="grid place-items-center mt-3.5" role="status" aria-label="Chargement">
                  <Squelette className="w-[176px] h-[176px]" rond />
                </div>
              ) : parFormule.length > 0 ? (
                <>
                  <div className="grid place-items-center mt-3.5">
                    <Anneau segments={parFormule} taille={176} legende="projets en cours" />
                  </div>
                  <div className="flex flex-col gap-2 mt-3.5">
                    {parFormule.map((f) => (
                      <div key={f.code || f.nom} className="flex items-center gap-2 text-[12.5px]">
                        <span className="w-2.5 h-2.5 rounded-[3px] shrink-0"
                              style={f.hachure
                                ? { background: 'repeating-linear-gradient(45deg,#B9C7BF 0 2px,#FFFFFF 2px 4px)',
                                    boxShadow: 'inset 0 0 0 1px #D5DFD9' }
                                : { background: f.couleur }} />
                        <span className="flex-1 min-w-0 truncate text-[var(--text-main)]">{f.nom}</span>
                        <span className="font-semibold text-[var(--text-bold)] tabular-nums">{f.valeur}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-[13px] text-[var(--text-muted)] text-center py-10">
                  Aucun projet n'est encore rattaché à une formule.
                </p>
              )}
            </article>
          </div>
        </div>

        {/* ── La colonne de droite ─────────────────────────────────
            La seule où l'on AGIT : ce qui attend une décision, puis le
            prochain rendez-vous — deux cartes empilées, comme dans le
            modèle. « À traiter » prend la hauteur restante pour que le
            bas des deux colonnes s'aligne.

            Chaque ligne y porte l'âge du plus ancien élément de son
            groupe : c'est l'attente qui dit l'urgence, pas le nombre. */}
        <div className="flex-[1_1_290px] min-w-0 flex flex-col gap-3.5">
          {/* Hauteur naturelle : « À traiter » ne s'étire pas. Une liste
              de quatre lignes qui occupe 350 px laisse un vide au milieu
              de la carte et fait descendre le rendez-vous hors de vue. */}
          <article className="shrink-0 rounded-carte bg-[var(--bg-card)]
                              shadow-carte p-5 pb-3">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="flex-1 min-w-0">
                <h2 className="titre-carte">À traiter</h2>
                <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                  {tdbAttendu ? 'Chargement…' : aTraiter.length === 0
                    ? 'Rien n’attend de décision'
                    : `${aTraiter.length} sujet${aTraiter.length > 1 ? 's' : ''} attendent une décision`}
                </p>
              </div>
              <button type="button" onClick={() => navigate('/demandes')}
                      className="act act-sm act-contour">
                Tout voir
              </button>
            </div>

            {tdbAttendu ? (
              <SqueletteListe lignes={3} className="!shadow-none" />
            ) : aTraiter.length === 0 ? (
              <div className="etat-vide">
                <span className="pastille past-vert mx-auto mb-3">
                  <Check className="w-5 h-5" strokeWidth={2} />
                </span>
                <p className="text-[15px] font-semibold text-[var(--text-bold)] m-0">
                  Tout est à jour
                </p>
                <p className="text-[13px] text-[var(--text-muted)] mt-1">
                  Aucune demande, aucun projet ni paiement en attente.
                </p>
              </div>
            ) : (
              <div className="flex flex-col">
                {aTraiter.map((item) => {
                  const Icone = ICONES_A_TRAITER[item.icone] || Inbox;
                  const teintes = {
                    alerte: 'bg-[var(--alerte-fond)] text-[var(--alerte)]',
                    attention: 'bg-[var(--attention-fond)] text-[var(--attention)]',
                    neutre: 'bg-[var(--info-fond)] text-[var(--text-bold)]',
                  };
                  return (
                    <button
                      key={item.cle}
                      type="button"
                      onClick={() => navigate(item.lien)}
                      className="flex items-center gap-3 w-full py-[11px] px-2 -mx-2 rounded-[14px]
                                 text-left hover:bg-[var(--bg-main)] transition-colors"
                    >
                      <span className={`w-10 h-10 shrink-0 rounded-pastille grid place-items-center
                                        ${teintes[item.ton] || teintes.neutre}`}>
                        <Icone className="w-5 h-5" strokeWidth={1.8} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-semibold text-[var(--text-bold)]">
                          {item.titre}
                        </span>
                        <span className="block text-[12.5px] text-[var(--text-muted)] truncate">
                          {item.detail}
                        </span>
                      </span>
                      {item.age != null && (
                        <span className={`text-[12px] font-semibold shrink-0 ${
                          item.age >= 7 ? 'text-[var(--alerte)]' : 'text-[var(--text-muted)]'}`}>
                          {item.age} j
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </article>

          {/* Elle prend la hauteur restante, et son contenu se centre
              dedans : c'est la carte qui s'étire, pas le vide sous le
              texte. Le bas des deux colonnes reste ainsi aligné quelle
              que soit la longueur de la liste au-dessus. */}
          <article className="flex-1 min-w-0 relative overflow-hidden rounded-carte
                              p-5 sur-sombre text-white flex flex-col justify-center"
                   style={{ background: 'linear-gradient(160deg, #12304A 0%, #0B1620 70%)' }}>
            <svg width="100%" height="100%" viewBox="0 0 320 240" preserveAspectRatio="xMidYMid slice"
                 aria-hidden="true" className="absolute inset-0 pointer-events-none">
              {[60, 78, 96, 114, 132, 150, 168, 186, 204].map((y, i) => (
                <path key={y} d={`M-10 ${y} C 70 ${y - 40}, 150 ${y + 50}, 330 ${y - 10}`}
                      fill="none" strokeWidth="1.2"
                      stroke={`rgba(94,189,79,${[0.10, 0.13, 0.16, 0.2, 0.24, 0.2, 0.16, 0.12, 0.09][i]})`} />
              ))}
            </svg>

            <div className="relative">
              <p className="m-0 text-[12px] font-semibold tracking-[.08em] uppercase text-[#9ED591]">
                Prochaine séance
              </p>

              {tdbAttendu ? (
                <div className="mt-3 opacity-60" role="status" aria-label="Chargement">
                  <SqueletteTexte lignes={2} />
                  <Squelette className="h-10 w-40 mt-5" />
                </div>
              ) : prochaine ? (
                <>
                  <p className="m-0 mt-2 text-[18px] font-semibold tracking-[-.015em] leading-[1.25]">
                    {prochaine.objet} — {prochaine.organisation}
                  </p>
                  <p className="m-0 mt-1 text-[12.5px] text-white/[.72]">
                    {prochaine.intervenant ? `Avec ${prochaine.intervenant} · ` : ''}
                    {quandSeance(prochaine.debut, prochaine.duree_minutes)}
                  </p>
                  <p className="m-0 mt-[18px] text-[40px] leading-none font-medium tracking-[-.02em]">
                    {compteARebours}
                  </p>
                  <div className="flex gap-2.5 mt-[18px]">
                    {prochaine.lien_visio ? (
                      <a href={prochaine.lien_visio} target="_blank" rel="noreferrer"
                         className="act flex-1 no-underline">
                        <Video className="w-[17px] h-[17px]" strokeWidth={2} />
                        Rejoindre
                      </a>
                    ) : (
                      <button type="button" className="act flex-1"
                              onClick={() => navigate(`/projets/${prochaine.projet_id}`)}>
                        <CalendarDays className="w-[17px] h-[17px]" strokeWidth={2} />
                        Ouvrir le projet
                      </button>
                    )}
                    <button type="button" aria-label="Voir toutes les séances"
                            onClick={() => navigate('/projets')}
                            className="w-11 h-11 shrink-0 rounded-controle grid place-items-center
                                       bg-white/[.12] text-white hover:bg-white/[.22] transition-colors">
                      <CalendarDays className="w-[18px] h-[18px]" strokeWidth={1.8} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="m-0 mt-2 text-[18px] font-semibold tracking-[-.015em] leading-[1.25]">
                    Aucune séance programmée
                  </p>
                  <p className="m-0 mt-1 mb-[18px] text-[12.5px] text-white/[.72]">
                    Aucun accompagnement n’a de rendez-vous à venir.
                  </p>
                  <button type="button" onClick={() => navigate('/projets')} className="act w-full">
                    <CalendarDays className="w-[17px] h-[17px]" strokeWidth={2} />
                    Programmer une séance
                  </button>
                </>
              )}
            </div>
          </article>
        </div>
      </div>


      {/* ── Analyse ──────────────────────────────────────────────────
          Trois lectures que les chiffres du haut ne donnent pas : où le
          profil pèche, où les demandes se perdent, et à quel rythme on
          accompagne réellement. */}
      <div className="flex flex-wrap gap-3.5">
        <article className="flex-[1_1_300px] min-w-0 rounded-carte bg-[var(--bg-card)]
                            shadow-carte px-[22px] pt-5 pb-[18px]">
          <h2 className="titre-carte">Profil moyen des diagnostics</h2>
          <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
            Score par dimension, sur 100 — pointillé : seuil de labellisation ({radar.seuil})
          </p>
          <div className="mt-2 min-h-[240px]">
            {tdbAttendu ? (
              <SqueletteGraphique className="h-[240px]" />
            ) : radar.axes.length >= 3 ? (
              <Radar axes={radar.axes} seuil={radar.seuil} />
            ) : (
              <p className="grid place-items-center h-[240px] text-[13px]
                            text-[var(--text-muted)] text-center">
                Pas encore assez de réponses pour dessiner un profil.
              </p>
            )}
          </div>
        </article>

        <article className="flex-[1_1_300px] min-w-0 rounded-carte bg-[var(--bg-card)]
                            shadow-carte px-[22px] py-5">
          <h2 className="titre-carte">Parcours des demandes</h2>
          <p className="mt-[3px] mb-[18px] text-[12.5px] text-[var(--text-muted)]">
            De la réception à la clôture, sur la période retenue
          </p>
          {tdbAttendu ? <SqueletteTexte lignes={5} /> : <Entonnoir etapes={parcours} />}
        </article>

        <article className="flex-[1.4_1_380px] min-w-0 rounded-carte bg-[var(--bg-card)]
                            shadow-carte px-[22px] pt-5 pb-[18px]">
          <div className="flex flex-wrap items-start gap-2.5">
            <div className="flex-[1_1_180px] min-w-0">
              <h2 className="titre-carte">Sessions de coaching</h2>
              <p className="mt-[3px] text-[12.5px] text-[var(--text-muted)]">
                16 dernières semaines{tdbAttendu ? '' : ` · ${seances.total} séance${seances.total > 1 ? 's' : ''}`}
              </p>
            </div>
            {/* L'échelle est indispensable : sans elle, cinq verts ne
                disent pas lequel vaut « beaucoup ». */}
            <div className="flex items-center gap-1 text-[11.5px] text-[var(--text-muted)]">
              <span className="mr-1">Moins</span>
              {['#EEF3EF', '#CDEAC5', '#9AD48C', '#5EBD4F', '#2F7A2A'].map((c) => (
                <span key={c} className="w-3 h-3 rounded-[4px]" style={{ background: c }} />
              ))}
              <span className="ml-1">Plus</span>
            </div>
          </div>
          <div className="mt-3.5 min-h-[200px] overflow-x-auto">
            {tdbAttendu
              ? <Squelette className="h-[180px] w-full !rounded-pastille" />
              : <Calendrier semaines={seances.semaines} />}
          </div>
        </article>

      </div>

      {/* ── Activité récente ─────────────────────────────────────
          Pleine largeur, comme dans le modèle : une liste de noms
          coincée dans une colonne de 420 px tronque précisément ce
          qui sert à la lire. */}
      <article className="w-full rounded-carte bg-[var(--bg-card)] shadow-carte
                          px-[22px] pt-5 pb-2.5">
        <div className="flex items-center gap-2.5 mb-1.5">
          <h2 className="titre-carte flex-1">Activité récente</h2>
          {/* Le journal est une DESTINATION, pas le contenu de la carte :
              ici on voit ce que font les organisations, là on retrace ce
              que l'équipe a fait. */}
          <button type="button" onClick={() => navigate('/messages')}
                  className="act act-sm act-contour">
            <History className="w-[15px] h-[15px]" strokeWidth={2} />
            Journal
          </button>
        </div>

        {tdbAttendu ? (
          <SqueletteListe lignes={4} className="!shadow-none" />
        ) : activite.length === 0 ? (
          <div className="etat-vide my-3">
            <span className="pastille past-nuit mx-auto mb-3">
              <Activity className="w-5 h-5" strokeWidth={1.8} />
            </span>
            <p className="text-[15px] font-semibold text-[var(--text-bold)] m-0">
              Rien ne s’est encore passé
            </p>
            <p className="text-[13px] text-[var(--text-muted)] mt-1">
              Demandes, diagnostics et paiements apparaîtront ici.
            </p>
          </div>
        ) : (
          /* Deux colonnes dès que la place le permet, une seule sinon :
             six lignes sur une colonne font défiler pour rien. */
          <div className="grid gap-x-7"
               style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))' }}>
            {activite.map((e, i) => (
              <button
                key={`${e.quand}-${i}`}
                type="button"
                onClick={() => navigate(e.lien)}
                className="flex items-center gap-3 py-3 text-left w-full
                           hover:opacity-80 transition-opacity"
              >
                <span className={`w-10 h-10 shrink-0 rounded-controle grid place-items-center
                                  text-[13px] font-bold ${TONS_ACTIVITE[e.ton] || TONS_ACTIVITE.cours}`}>
                  {(e.organisation || '?').slice(0, 2).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-semibold text-[var(--text-bold)] truncate">
                    {e.organisation}
                  </span>
                  <span className="block text-[12.5px] text-[var(--text-muted)] truncate">
                    {e.action}{' '}
                    <b className="font-semibold text-[var(--text-bold)]">{e.objet}</b>
                  </span>
                </span>
                <span className={`etiquette shrink-0 etiq-${e.ton}`}>{e.etat}</span>
                <span className="w-10 shrink-0 text-right text-[12px] text-[var(--text-muted)]">
                  {depuis(e.quand)}
                </span>
              </button>
            ))}
          </div>
        )}
      </article>
      </div>

  );
}
